/* eslint-disable complexity */
/* eslint-disable max-statements */
import { Preferences } from '@3ds/common-preferences'
import { DS_LANG } from '@ds/swymkit/config'
import { addHookConfig, addHooksAfterRequireSetup } from '@ds/swymkit/tools'
import type { RequireConfig } from '@ds/swymkit/typings'

import { DS_RESOURCE_URL, DS_WEBAPPS_URL } from '~/config/global-var'

/** cache bust file path. It includes information to bust the cache after an update/upgrade */
const CACHE_BUST_PATH = 'SwymVueWeb/dist/cache-bust.json'

/**
 * A string generated from cache-bust.json
 * @default "NO_CACHE_ID" value is set to show easily that the cache bust generation failed
 */
let CACHE_BUST_ARG = `bust=NO_CACHE_ID`

async function setupRequireConfig(): Promise<Partial<RequireConfig>> {
  // Require configurations
  // Path starts with "." to be relative to the baseUrl
  const paths = {
    'DS': '.',
    'vuejs': `./vuejs/2.6.10/vue.min`,
    'vu-kit': `./vuekit/vu-kit.umd`, // have to call it "vu-kit"
    'vuex': `./vuex/3.1.1/vuex.min`,
    'Vuetify': `./vuetify-2.7.1/dist/vuetify.min`,
    'SwymUIVueComponents': `./SwymUIVueComponents/dist/SwymUIVueComponents.umd`,
    'SwymUIVueComponentsCSS': `./SwymUIVueComponents/dist/SwymUIVueComponents`,
  }

  const configs = Preferences.getNativeAppRequireConfig()
  if (configs)
    Object.assign(paths, configs.requirePaths)

  try {
    const response = await fetch(`${DS_WEBAPPS_URL}${CACHE_BUST_PATH}?bust=${(Date.now() / 1000).toFixed(0)}`)
    const { BUILD_TIME, CACHE_ID, VERSION_COMPONENT, VERSION_SERVICE } = await response.json() as {
      BUILD_TIME: string
      CACHE_ID?: string
      VERSION_COMPONENT?: string
      VERSION_SERVICE?: string
    }

    const bustArgs = []
    if (BUILD_TIME)
      bustArgs.push(`build=${BUILD_TIME}`)
    if (CACHE_ID)
      bustArgs.push(`cache=${CACHE_ID}`)
    if (VERSION_COMPONENT)
      bustArgs.push(`component=${VERSION_COMPONENT}`)
    if (VERSION_SERVICE)
      bustArgs.push(`service=${VERSION_SERVICE}`)

    if (bustArgs.length)
      CACHE_BUST_ARG = bustArgs.join('&')
  }
  catch {
    /** The cache busting file does not exist. Skip. */
  }

  if (!window.require) {
    // this is the require config object and not the require function itself
    return {
      urlArgs: CACHE_BUST_ARG,
      baseUrl: DS_WEBAPPS_URL,
      waitSeconds: 30,
      paths: {
        ...paths,
        'ds': `${DS_RESOURCE_URL}resources/0/0/wp/script`,
        'DS/UWPClientCode/UWA': 'UWA2/js',
        'UWA': 'UWA2/js',
        'vendors': 'vendors',
      },
      shim: {
        'DS/PlatformAPI/PlatformAPI': ['DS/UWPClientCode/PublicAPI'],
      },
      config: {
        'i18n': {
          locale: DS_LANG,
        },
        'DS/Logger/Logger': {
          disableLog: !false,
        },
      },
      skipDataMain: true,
    }
  }
  // @ts-expect-error require is always define but maybe not, this is worth checking
  if (window.require && window.widget) {
    return {
      urlArgs: CACHE_BUST_ARG,
      paths,
    }
  }
  return {}
}

export function registerRequireHooks(): void {
  addHookConfig(async (config) => {
    return { ...config, ...(await setupRequireConfig()) }
  })

  addHooksAfterRequireSetup(async () => {
    // TODO We should document why this alias is needed !
    window.define('vue', ['vuejs'], Vue => Vue)
    // To load widget in Swym env
    window.define('DS/UWPClientCode/UWA/Environment', ['DS/SwymStandalone/environment'], Env => Env)
    // To load Story
    window.require(['DS/UWPClientCode/Navigation', 'DS/UWPClientCode/Bootstrap'])
  })
}
